import { useEffect, useState } from 'react';
import { App, Form, Input, InputNumber, Modal, Switch } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rechargeCredits, setUnlimited, usageQueryKeys } from '../../api/usage';
import { isPositiveCreditAmount, normalizeCreditAmount } from './format';

function extractErrorMessage(e: unknown, fallback: string): string {
  return (
    (e as { response?: { data?: { message?: string } } })?.response?.data?.message
    ?? (e as Error)?.message
    ?? fallback
  );
}

function newIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `recharge-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

interface Props {
  tenantId: number;
  isUnlimited: boolean;
  canWriteMode: boolean;
}

export function CreditActions({ tenantId, isUnlimited, canWriteMode }: Props) {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<{ amount: string; reason: string }>();
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);
  const [rechargeError, setRechargeError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setIdempotencyKey(newIdempotencyKey());
      setRechargeError(null);
    }
  }, [open]);

  function invalidateUsage() {
    void qc.invalidateQueries({ queryKey: usageQueryKeys.root });
  }

  const rechargeMut = useMutation({
    mutationFn: (body: { amount: string; reason: string }) => rechargeCredits(tenantId, body, idempotencyKey),
    onSuccess: () => {
      message.success('充值成功');
      setRechargeError(null);
      setOpen(false);
      form.resetFields();
      invalidateUsage();
    },
    onError: (e) => setRechargeError(extractErrorMessage(e, '充值失败')),
  });

  const modeMut = useMutation({
    mutationFn: (enabled: boolean) => setUnlimited(tenantId, { enabled, reason: enabled ? '开启不限量' : '关闭不限量' }),
    onSuccess: () => {
      message.success('模式已更新');
      invalidateUsage();
    },
    onError: (e) => message.error(extractErrorMessage(e, '切换失败')),
  });

  async function submitRecharge() {
    try {
      const values = await form.validateFields();
      const amount = normalizeCreditAmount(String(values.amount ?? ''));
      if (!amount) {
        form.setFields([{ name: 'amount', errors: ['金额必须为正数且最多六位小数'] }]);
        return;
      }
      await rechargeMut.mutateAsync({ amount, reason: String(values.reason ?? '').trim() });
    } catch {
      /* 校验失败或请求失败时保留弹窗与重试 */
    }
  }

  function confirmMode(enabled: boolean) {
    modal.confirm({
      title: enabled ? '开启不限量' : '关闭不限量',
      content: '切换后保留余额；在途调用按结算时模式处理。前端按钮约束不代替后端 403。',
      okText: '确认',
      cancelText: '取消',
      onOk: () => modeMut.mutateAsync(enabled),
    });
  }

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      <button type="button" className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>充值</button>
      {canWriteMode && (
        <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <span>不限量</span>
          <Switch
            checked={isUnlimited}
            loading={modeMut.isPending}
            onChange={confirmMode}
          />
        </label>
      )}
      <Modal
        open={open}
        title="充值"
        footer={[
          <button key="cancel" type="button" className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setRechargeError(null); }}>取消</button>,
          <button key="ok" type="button" className="btn btn-primary btn-sm" disabled={rechargeMut.isPending} onClick={() => void submitRecharge()}>确认充值</button>,
        ]}
        onCancel={() => { setOpen(false); setRechargeError(null); }}
        destroyOnHidden
        transitionName=""
        maskTransitionName=""
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="amount"
            label="充值积分"
            rules={[{
              validator: async (_, value) => {
                if (!isPositiveCreditAmount(String(value ?? ''))) throw new Error('金额必须为正数且最多六位小数');
              },
            }]}
          >
            <InputNumber
              stringMode
              controls={false}
              keyboard={false}
              changeOnBlur={false}
              style={{ width: '100%' }}
              placeholder="最多六位小数"
              parser={(v) => (v ?? '').replace(/[^\d.-]/g, '')}
              formatter={(v) => String(v ?? '')}
            />
          </Form.Item>
          <Form.Item name="reason" label="原因" rules={[{ required: true, message: '原因不能为空' }]}>
            <Input />
          </Form.Item>
        </Form>
        {rechargeError && (
          <div className="usage-error">
            <span>{rechargeError}</span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={submitRecharge}>重试</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
